import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LifeBuoy, RefreshCw, Send, X } from 'lucide-react';
import { api } from '../../api/client';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';
import { useLivePoll } from '../../hooks/useLivePoll';

/**
 * Prijave problema - pregled i odgovor.
 *
 * Korisnik prijavljuje problem iz aplikacije, ali do sada tu prijavu niko iz
 * podrške nije mogao da vidi: sve rute su bile tenant-scoped, pa je jedini put
 * do prijave bio email - koji ne stiže ako SMTP nije podešen. Tiketi su tiho
 * stajali u bazi.
 *
 * Spisak se osvežava sam: nova prijava iskače bez klika na „Osveži". Panel nema
 * SSE kanal kao kasa (EventSource ne ume `Authorization` header, a sysadmin token
 * u URL-u završava po logovima), pa se anketira - i to samo dok neko gleda.
 *
 * Ekran kreće od **aktivnih** prijava - otvorenih i onih u radu. Prva verzija je
 * pokazivala samo „otvorene", pa je tiket nestajao čim mu podrška odgovori:
 * odgovor ga pomera u „u radu". Čovek koji je upravo odgovorio video bi da mu je
 * prijava iščezla sa spiska.
 */

type Ticket = {
  id: string;
  tenantId: string;
  tenantName: string;
  subject: string;
  message: string;
  severity: 'low' | 'normal' | 'high' | 'critical';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  url: string | null;
  appVersion: string | null;
  contactEmail: string | null;
  emailSent: boolean;
  createdAt: string;
  updatedAt: string;
};

type Message = {
  id: string;
  authorType: 'tenant' | 'support';
  authorName: string | null;
  body: string;
  createdAt: string;
};

type TicketDetail = Ticket & { messages: Message[]; userEmail: string | null; tenantSuspended: boolean | null };

const SEVERITY: Record<Ticket['severity'], { label: string; color: string }> = {
  low: { label: 'Nizak', color: '#94a3b8' },
  normal: { label: 'Normalan', color: '#60a5fa' },
  high: { label: 'Visok', color: '#fbbf24' },
  critical: { label: 'Kritičan', color: '#f87171' },
};

const STATUS: Record<Ticket['status'], { label: string; color: string }> = {
  open: { label: 'Otvoren', color: '#f87171' },
  in_progress: { label: 'U radu', color: '#fbbf24' },
  resolved: { label: 'Rešen', color: '#4ade80' },
  closed: { label: 'Zatvoren', color: '#94a3b8' },
};

const fmt = (iso: string) => new Date(iso).toLocaleString('sr-Latn-RS');

/** Na kolko se spisak i otvoren razgovor osvežavaju dok je kartica vidljiva. */
const POLL_MS = 5_000;

export function SupportTicketsPage() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Podrazumevano AKTIVNI (otvoren + u radu), ne samo „otvoren".
  //
  // Sa `open` je tiket nestajao cim mu podrska odgovori - odgovor ga pomera u
  // „u radu". Covek koji je upravo odgovorio video bi da mu je prijava iscezla.
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<TicketDetail | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Otvoren tiket se pamti po ID-u: anketiranje mora da zna šta da osveži i kad
  // se sam objekat u međuvremenu promenio.
  const selectedId = selected?.id ?? null;

  /**
   * `silent` - osvežavanje u pozadini ne pali indikator učitavanja.
   *
   * Bez toga bi spisak treperio na svakih nekoliko sekundi i čovek bi izgubio
   * mesto na kom je stao.
   */
  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (severityFilter) params.set('severity', severityFilter);
      if (query.trim()) params.set('q', query.trim());
      const res = await api.get<{ tickets: Ticket[]; total: number }>('/admin/support?' + params.toString());
      setTickets(res.tickets);
      setTotal(res.total);
    } catch (e: any) {
      setError(e?.data?.message || 'Učitavanje prijava nije uspelo.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [statusFilter, severityFilter, query]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Nove prijave iskaču same; otvoren razgovor se osvežava zajedno sa spiskom.
  useLivePoll(async () => {
    await refresh(true);
    if (selectedId) await open(selectedId, true);
  }, POLL_MS);

  const open = useCallback(async (id: string, silent = false) => {
    try {
      setSelected(await api.get<TicketDetail>(`/admin/support/${id}`));
    } catch (e: any) {
      // Tiho osvežavanje ne prijavljuje grešku: prolazan prekid mreže ne treba
      // da baci crvenu poruku preko razgovora koji čovek čita.
      if (!silent) setError(e?.data?.message || 'Otvaranje prijave nije uspelo.');
    }
  }, []);

  /**
   * `?ticket=<id>` u adresi otvara tu prijavu.
   *
   * Tako klik na obaveštenje vodi PRAVO u razgovor, spreman za odgovor - a ne
   * na spisak sa kog čovek opet traži prijavu koju je upravo video. Radi i kad
   * je već na spisku: menja se adresa, pa se okida i ovde.
   *
   * Prijava se dovlači po ID-u, pa se otvara i kad je izvan tekućeg filtera
   * (npr. zatvorena, a spisak pokazuje aktivne).
   */
  const requestedId = searchParams.get('ticket');
  const openedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!requestedId || openedFor.current === requestedId) return;
    openedFor.current = requestedId;
    void open(requestedId);
    // Adresa se čisti da osvežavanje stranice ne bi večno vraćalo isti tiket.
    setSearchParams({}, { replace: true });
  }, [requestedId, open, setSearchParams]);


  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <LifeBuoy size={20} />
        <h1 style={{ margin: 0, fontSize: 20 }}>Prijave problema</h1>
        <span style={{ color: '#94a3b8', fontSize: 13 }}>{total} ukupno</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => void refresh()} disabled={loading} style={btn} data-testid="support-refresh">
          <RefreshCw size={14} /> Osveži
        </button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={field} data-testid="support-status">
          <option value="active">Aktivni (otvoreni + u radu)</option>
          <option value="">Svi statusi</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} style={field}>
          <option value="">Svi prioriteti</option>
          {Object.entries(SEVERITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Pretraga po naslovu ili opisu"
          style={{ ...field, minWidth: 240, flex: 1 }}
          data-testid="support-search"
        />
      </div>

      {error && <div style={{ padding: 10, background: 'rgba(248,113,113,0.12)', color: '#f87171', borderRadius: 6 }}>{error}</div>}

      <div style={{ border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#0f172a' }}>
              <th style={th}>Klijent</th>
              <th style={th}>Naslov</th>
              <th style={th}>Prioritet</th>
              <th style={th}>Status</th>
              <th style={th}>Prijavljeno</th>
            </tr>
          </thead>
          <tbody>
            {!loading && tickets.length === 0 && (
              <tr><td colSpan={5} style={{ ...td, textAlign: 'center', padding: 32, color: '#64748b' }}>
                Nema prijava za izabrane filtere.
              </td></tr>
            )}
            {tickets.map((t) => (
              <tr key={t.id} onClick={() => open(t.id)} style={{ cursor: 'pointer' }} data-testid={`support-row-${t.id}`}>
                <td style={td}>{t.tenantName}</td>
                <td style={{ ...td, color: '#e2e8f0' }}>{t.subject}</td>
                <td style={td}><Badge {...SEVERITY[t.severity]} /></td>
                <td style={td}><Badge {...STATUS[t.status]} /></td>
                <td style={{ ...td, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmt(t.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <TicketDialog
          ticket={selected}
          onClose={() => setSelected(null)}
          onChanged={async () => { await refresh(true); await open(selected.id); }}
        />
      )}
    </div>
  );
}

function TicketDialog({ ticket, onClose, onChanged }: {
  ticket: TicketDetail;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}) {
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const replyRef = useRef<HTMLTextAreaElement>(null);

  useEscapeToClose(onClose);

  // Otvoren razgovor je otvoren da bi se odgovorilo - polje odmah prima kucanje.
  useEffect(() => { replyRef.current?.focus(); }, []);

  const send = async () => {
    const body = reply.trim();
    if (!body) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/admin/support/${ticket.id}/messages`, { body });
      setReply('');
      await onChanged();
    } catch (e: any) {
      setError(e?.data?.message || 'Slanje odgovora nije uspelo.');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status: Ticket['status']) => {
    setBusy(true); setError(null);
    try {
      await api.put(`/admin/support/${ticket.id}/status`, { status });
      await onChanged();
    } catch (e: any) {
      setError(e?.data?.message || 'Promena statusa nije uspela.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
      <div style={{ width: 'min(720px, 94vw)', maxHeight: '90vh', background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid #1e293b' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, color: '#e2e8f0' }}>{ticket.subject}</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>
              {ticket.tenantName}
              {ticket.tenantSuspended ? ' · SUSPENDOVAN' : ''}
              {ticket.userEmail ? ` · ${ticket.userEmail}` : ''}
              {' · '}{fmt(ticket.createdAt)}
            </div>
          </div>
          <Badge {...SEVERITY[ticket.severity]} />
          <Badge {...STATUS[ticket.status]} />
          <button onClick={onClose} style={{ ...btn, padding: 6 }}><X size={16} /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ background: '#111c30', borderRadius: 8, padding: 12, whiteSpace: 'pre-wrap', color: '#cbd5e1', fontSize: 13 }}>
            {ticket.message}
          </div>

          {/* Okolnosti prijave - bez njih se greska trazi naslepo. */}
          {(ticket.url || ticket.appVersion || ticket.contactEmail) && (
            <div style={{ fontSize: 12, color: '#64748b', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {ticket.url && <span>Ekran: {ticket.url}</span>}
              {ticket.appVersion && <span>Verzija: {ticket.appVersion}</span>}
              {ticket.contactEmail && <span>Kontakt: {ticket.contactEmail}</span>}
              {!ticket.emailSent && <span style={{ color: '#fbbf24' }}>email nije poslat</span>}
            </div>
          )}

          {ticket.messages.map((m) => (
            <div
              key={m.id}
              style={{
                alignSelf: m.authorType === 'support' ? 'flex-end' : 'flex-start',
                maxWidth: '80%',
                background: m.authorType === 'support' ? '#1d4ed8' : '#1e293b',
                color: '#e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 13, whiteSpace: 'pre-wrap',
              }}
            >
              <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 2 }}>
                {m.authorName || (m.authorType === 'support' ? 'Podrška' : 'Klijent')} · {fmt(m.createdAt)}
              </div>
              {m.body}
            </div>
          ))}
        </div>

        {error && <div style={{ padding: '0 18px 8px', color: '#f87171', fontSize: 12 }}>{error}</div>}

        <div style={{ borderTop: '1px solid #1e293b', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <textarea
              ref={replyRef}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => {
                // Ctrl/Cmd+Enter salje - ruke ostaju na tastaturi.
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); void send(); }
              }}
              placeholder="Odgovor klijentu…"
              rows={2}
              style={{ ...field, flex: 1, resize: 'vertical' }}
              data-testid="support-reply"
            />
            <button onClick={send} disabled={busy || !reply.trim()} style={{ ...btn, alignSelf: 'stretch' }} data-testid="support-send">
              <Send size={14} /> Pošalji
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(Object.keys(STATUS) as Ticket['status'][]).map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                disabled={busy || ticket.status === s}
                style={{ ...btn, opacity: ticket.status === s ? 0.5 : 1 }}
                data-testid={`support-status-${s}`}
              >
                {STATUS[s].label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: `${color}22`, color, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

const th: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', fontSize: 12, color: '#94a3b8', borderBottom: '1px solid #1e293b' };
const td: React.CSSProperties = { padding: '8px 12px', fontSize: 13, color: '#cbd5e1', borderBottom: '1px solid #16202f' };
const btn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '7px 12px', borderRadius: 6, border: '1px solid #1e293b',
  background: '#111c30', color: '#e2e8f0', fontSize: 13, cursor: 'pointer',
};
const field: React.CSSProperties = {
  padding: '7px 10px', borderRadius: 6, border: '1px solid #1e293b',
  background: '#0b1220', color: '#e2e8f0', fontSize: 13,
};
